import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Search,
  MoreHorizontal,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  X,
  Copy,
  SquareArrowOutUpRight,
  Funnel,
  Pencil,
  PencilLine,
  Plus,
  CircleAlert,
  CircleX,
  ArrowRight,
  ChevronDown,
  Info,
} from "lucide-react";
import { useHistory, useLocation } from "react-router-dom";
import noBrandImage from "../../assets/img/brand/no-brand-img.png";
import emptySearchImg from "../../assets/img/brand/empty-search.png";
import Pagination from "../../components/Pagination";
import SortableHeader from "../../components/SortableHeader";
import SearchBox from "../../components/SearchBox";
import FilterDrawer from "../../components/FilterDrawer";
import { formatDateTime } from "../../utils/formateDate";
import ConfirmDeactivateModal from "../../components/ConfirmDeactivateModal";
import AlertNotification from "../../components/AlertNotification";
import apiHelper from "../../apiHelper";
import noImage from "../../assets/img/brand/noImage.png";
import { useLoader } from '../../components/GlobalLoader';

const PAGE_SIZE = 20;
const PH_PAGE_SIZE = 10;

/* ---------- Helper (text color mapping for sync date) ---------- */
function getPlatformSyncStatus(p) {
  const hasSync = !!p?.lastSync && p?.lastSync !== "-";
  const hasPlatformPrice = p?.platformPrice && p?.platformPrice !== "-";
  const hasErpPrice = p?.erpPrice && p?.erpPrice !== "-";
  const backendStatus = p?.syncstatus;

  // Test Sync failure has no last_sync_at (polling never ran) → grey
  // Polling failure has last_sync_at set → red
  if (backendStatus === "failed") {
    return hasSync ? "FAILED" : "NEVER";
  }

  // Failed
  if (
    p.lastSync?.toLowerCase?.().includes("failed") ||
    (!hasPlatformPrice && !hasErpPrice && hasSync)
  ) {
    return "FAILED";
  }

  // Never polled
  // if (!hasSync && !hasPlatformPrice && !hasErpPrice) {
  if (!hasSync && !hasPlatformPrice) {
    return "NEVER";
  }

  // Success
  // if (hasPlatformPrice && hasErpPrice) {
  if (hasPlatformPrice) {
    const platformNum = parseFloat(p?.platformPrice.replace(/[^\d.]/g, ""));
    const erpNum = parseFloat(p?.erpPrice.replace(/[^\d.]/g, ""));

    if (!isNaN(platformNum) && platformNum === erpNum) {
      return "MATCH";
    }
    return "MISMATCH";
  }

  return "NEVER";
}

function formatSGD(value) {
  if (value === null || value === undefined || value === "") return "-";

  const num = Number(value);
  if (Number.isNaN(num)) return "-";

  return `SGD ${num.toFixed(2)}`;
}

function getPlatformVisual(status) {
  switch (status) {
    case "MATCH":
      return {
        dotColor: "#146C43", // green
        textColor: "#212529", // black
      };
    case "MISMATCH":
      return {
        dotColor: "#CC9A06", // amber
        textColor: "#664D03", // amber text
      };
    case "FAILED":
      return {
        dotColor: "#B02A37", // red
        textColor: "#58151C", // red text
      };
    case "NEVER":
    default:
      return {
        dotColor: "#CED4DA", // gray
        textColor: "#212529", // black
      };
  }
}

// const noImage = "https://via.placeholder.com/28?text=img";


export default function PlatformPricingTest(props) {
  // --- Breadcrumbs & React Router ---
  const breadcrumbs = ["Pricing", "Platform Pricing (Test - Prefetch)"];
  const history = useHistory();
  const location = useLocation();
  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [selectedStockId, setSelectedStockId] = useState(null);

  const [notification, setNotification] = useState(null);

  // --- Tab State ---
  const [activeTab, setActiveTab] = useState("initiate-pricing");

  useEffect(() => {
    const incomingTab = location?.state?.activeTab;
    if (incomingTab) {
      setActiveTab(incomingTab);
      history.replace(location.pathname, {}); // clear state
    }
  }, [location.state, location.pathname, history]);

  // --- Search / Pagination / Selection State ---
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [suppressHighlight, setSuppressHighlight] = useState(false);
  const filterButtonRef = useRef(null);
  const [filterPopupOpen, setFilterPopupOpen] = useState(false);
  const [phFilterPopupOpen, setPhFilterPopupOpen] = useState(false);

  // --- Drawer / Row State ---
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerTab, setDrawerTab] = useState(null);
  const drawerRef = useRef(null);
  const { showLoader, hideLoader } = useLoader();

  const [listings, setListings] = useState([]);
  const [pricingHistory, setPricingHistory] = useState([]);

  const [tempValues, setTempValues] = useState({
    platforms: [],
    platformPrice_min: "",
    platformPrice_max: "",
    erpPrice_min: "",
    erpPrice_max: "",
  });
 
  const [filters, setFilters] = useState({
    platforms: [],
    platformPrice_min: "",
    platformPrice_max: "",
    erpPrice_min: "",
    erpPrice_max: "",
        
    });

  const [phTempValues, setPhTempValues] = useState({
    changesTime_start: null,
    changesTime_end: null,
  });

  const [phAppliedFilter, setPhAppliedFilter] = useState(null);

  // --- Bulk Pricing State ---
  const BULK_PAGE_SIZE = 10;
  const [bulkActive, setBulkActive] = useState(false);
  const [bulkItems, setBulkItems] = useState([]);
  const [bulkPage, setBulkPage] = useState(1);
  const [bulkDropdownOpen, setBulkDropdownOpen] = useState(false);
  const bulkToolbarRef = useRef(null);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);

  // --- Toolbar Dropdown State ---
  const [toolbarDropdownOpen, setToolbarDropdownOpen] = useState(false);
  const toolbarRef = useRef(null);
  const [copied, setCopied] = useState(false);

  // --- Per-row Dropdown State ---
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef(null);
  const buttonRefs = useRef({});
  const [sortField, setSortField] = useState("last_modified_time");
  const [sortOrder, setSortOrder] = useState("desc");
  const [totalCount, setTotalCount] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ── Prefetch ──────────────────────────────────────────────────────
  const prefetchCache = useRef(new Map());
  const prefetchInProgress = useRef(new Set());
  const isFirstSearchRender = useRef(true);
  const isFirstFilterRender = useRef(true);

  // --- Helpers ---
  function parseCurrencyToNumber(str) {
    if (!str) return NaN;
    const m = String(str).replace(/[^\d.-]/g, "");
    return m === "" ? NaN : Number(m.replace(/,/g, ""));
  }


  // ── Server-side helpers ───────────────────────────────────────────
  function buildPricingCacheKey(page, search, filters, sf, so) {
    return JSON.stringify({ page, search: (search || "").trim(), filters, sf, so });
  }

  function buildPricingOrdering(sf, so) {
    if (!sf) return '-modified_at';
    return `${so === 'desc' ? '-' : ''}${sf}`;
  }

  async function prefetchPricingPage(page, search, filters, sf, so, totalCnt) {
    const totalPgs = Math.ceil(totalCnt / PAGE_SIZE);
    if (page > totalPgs) return;
    const key = buildPricingCacheKey(page, search, filters, sf, so);
    if (prefetchCache.current.has(key) || prefetchInProgress.current.has(key)) return;
    prefetchInProgress.current.add(key);
    try {
      const params = new URLSearchParams({ page, page_size: PAGE_SIZE });
      if (search && search.trim()) params.append('search', search.trim());
      if (filters?.platforms?.length) params.append('platform', filters.platforms.join(','));
      if (filters?.platformPrice_min !== "") params.append('platform_price_min', filters.platformPrice_min);
      if (filters?.platformPrice_max !== "") params.append('platform_price_max', filters.platformPrice_max);
      if (filters?.erpPrice_min !== "") params.append('erp_price_min', filters.erpPrice_min);
      if (filters?.erpPrice_max !== "") params.append('erp_price_max', filters.erpPrice_max);
      params.append('ordering', buildPricingOrdering(sf, so));
      const response = await apiHelper(`sales/initiate-pricing/?${params.toString()}`, "GET");
      if (!response.ok) return;
      const json = await response.json();
      const results = (json.results || []).map(mapInitiatePricingApiToUi);
      prefetchCache.current.set(key, { results, count: json.count || 0 });
    } catch (_) { /* silent */ } finally {
      prefetchInProgress.current.delete(key);
    }
  }

  // Server already handles all filtering/sorting — listings is the current page
  const filtered = listings;


  const getPlatformColor = (platformName = "") => {
    if (platformName.includes("Shopee")) return "#EE4D2D";
    if (platformName.includes("Lazada")) return "#1A9BF7";
    if (platformName.includes("TikTok")) return "#000000";
    return "#6C757D"; // default gray
  };

  const mapInitiatePricingApiToUi = (item) => ({
    id: item.sales_listing_id,

    name: item.spu_name || "-",
    variant: item.sku_name
      ? (() => {
        const parts = item.sku_name.split("-");
        const name = parts[0];
        const storage = parts.find(p => /GB/i.test(p));
        return storage
          ? `${name}, ${storage.replace(/\s+/g, "").toUpperCase()}`
          : name;
      })()
      : "-",

    avatar: item.sku_image || noImage,

    condition: item.condition || "-",
    grading: item.condition_type || "-",
    listing_code: item.listing_code,

    last_modified_time: formatDateTime(item.modified_at),

    status: "Active",

    basePrice: formatSGD(item.base_price),
    erpStock: "-",

    platforms: item.platforms.map((p) => {
      const platformObj = {
        id: p.platform_id,
        sales_listing_mapping_id: p.sales_listing_mapping_id,
        name: p.platform_name,
        color: getPlatformColor(p.platform_name),

        // platformPrice:
        //   p.platform_price !== null ? `SGD ${p.platform_price}` : "-",
        platformPrice: formatSGD(p.platform_price),
        erpPrice: formatSGD(p.override_price),
        syncstatus: p.sync_status,
        // erpPrice: p.override_price !== null ? `SGD ${p.override_price}` : "-",

        platformStock: "-",
        lastSync: formatDateTime(p.last_sync_time) == 'NA' ? '-' : formatDateTime(p.last_sync_time),
      };

      const status = getPlatformSyncStatus(platformObj);
      const visual = getPlatformVisual(status);

      return {
        ...platformObj,
        platformPrice: status === "FAILED" ? "-" : platformObj.platformPrice,
        syncStatus: status,
        dotColor: visual.dotColor,
        textColor: visual.textColor,
      };
    }),
  });

  const mapPricingHistoryApiToUi = (history) => ({
    id: history.sales_listing_price_history_id,

    time: history.changes_time,
    initiator: history.initiator || "Unknown",

    initiator: {
      brandName: history.initiator,
    },

    reason: history.reason,

    changeCount: history.items.length,

    changes: history.items.map((item, index) => ({
      id: `${history.sales_listing_price_history_id}-${index}`,

      stockName: item.spu,
      variant: item.sku
        ? (() => {
          const parts = item.sku.split("-");
          const name = parts[0];
          const storage = parts.find(p => /GB/i.test(p));
          return storage
            ? `${name}, ${storage.replace(/\s+/g, "").toUpperCase()}`
            : name;
        })()
        : "-",

      condition: item.condition || "-",
      grading: item.condition_type || "-",

      avatar: item.sku_image || noImage,
      baseBefore:
        Number(item.base_price_before) === 0 ||
          item.base_price_before == null ||
          item.base_price_before === ""
          ? "-"
          : `SGD ${Number(item.base_price_before).toFixed(2)}`,
      baseAfter: `SGD ${item.base_price_after}`,

      listing_code: item.listing_code,

      platforms: item.platforms.map((p) => {
        const before = Number(p.platform_price_before);
        const after = Number(p.platform_price_after);

        const isFirstTime =
          before === 0 ||   // 🔥 IMPORTANT
          p.platform_price_before === null ||
          p.platform_price_before === undefined ||
          p.platform_price_before === "";

        return {
          name: p.platform,

          beforePrice: isFirstTime
            ? "-"   // ✅ convert 0 → "-"
            : `SGD ${before.toFixed(2)}`,

          afterPrice: `SGD ${after.toFixed(2)}`,
        };
      })
    })),
  });


  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const pageData = listings;

  // --- Selection Handlers ---
  const toggleSelect = (id) => {
    const isRemoving = selectedIds.includes(id);
    setSelectedIds((ids) => {
      const next = isRemoving ? ids.filter((x) => x !== id) : [...ids, id];
      setSuppressHighlight(false);
      return next;
    });
    if (isRemoving && bulkItems.includes(id)) {
      setBulkItems((prev) => {
        const next = prev.filter((x) => x !== id);
        if (next.length === 0) {
          setBulkDropdownOpen(false);
          setBulkActive(false);
        }
        return next;
      });
    }
  };
  const handleDeactivateStock = (stockId) => {
    console.log("Activate / Deactivate stock id:", stockId);

    // 👇 yahan aapki API call aayegi
    // dispatch(deactivateStock(stockId))
  };

  const toggleAll = () => {
    const allOnPage = pageData.map((d) => d.id);
    const alreadyAllSelected =
      allOnPage.length > 0 && allOnPage.every((id) => selectedIds.includes(id));
    setSelectedIds((prev) => {
      const next = alreadyAllSelected
        ? prev.filter((id) => !allOnPage.includes(id))
        : [...new Set([...prev, ...allOnPage])];
      setSuppressHighlight(false);
      return next;
    });
    if (alreadyAllSelected) {
      setBulkItems((prev) => {
        const next = prev.filter((id) => !allOnPage.includes(id));
        if (next.length === 0) {
          setBulkDropdownOpen(false);
          setBulkActive(false);
        }
        return next;
      });
    }
  };
  const clearSelection = () => {
    setSelectedIds([]);
    setBulkDropdownOpen(false);
    setBulkActive(false);
    setBulkItems([]);
  };

  // ── Reactive server-side fetch ────────────────────────────────────
  useEffect(() => {
    async function load() {
      const key = buildPricingCacheKey(currentPage, debouncedSearch, filters, sortField, sortOrder);
      const cached = prefetchCache.current.get(key);
      if (cached) {
        setListings(cached.results);
        setTotalCount(cached.count);
        prefetchPricingPage(currentPage + 1, debouncedSearch, filters, sortField, sortOrder, cached.count);
        return;
      }
      showLoader();
      try {
        const params = new URLSearchParams({ page: currentPage, page_size: PAGE_SIZE });
        if (debouncedSearch && debouncedSearch.trim()) params.append('search', debouncedSearch.trim());
        if (filters?.platforms?.length) params.append('platform', filters.platforms.join(','));
        if (filters?.platformPrice_min !== "") params.append('platform_price_min', filters.platformPrice_min);
        if (filters?.platformPrice_max !== "") params.append('platform_price_max', filters.platformPrice_max);
        if (filters?.erpPrice_min !== "") params.append('erp_price_min', filters.erpPrice_min);
        if (filters?.erpPrice_max !== "") params.append('erp_price_max', filters.erpPrice_max);
        params.append('ordering', buildPricingOrdering(sortField, sortOrder));
        const response = await apiHelper(`sales/initiate-pricing/?${params.toString()}`, "GET");
        if (!response.ok) throw new Error("Failed to fetch initiate pricing");
        const json = await response.json();
        const mapped = (json.results || []).map(mapInitiatePricingApiToUi);
        prefetchCache.current.set(key, { results: mapped, count: json.count || 0 });
        setListings(mapped);
        setTotalCount(json.count || 0);
        prefetchPricingPage(currentPage + 1, debouncedSearch, filters, sortField, sortOrder, json.count || 0);
      } catch (error) {
        console.error(error);
      } finally {
        hideLoader();
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearch, filters, sortField, sortOrder]);

  // ── Debounce search input ─────────────────────────────────────────
  useEffect(() => {
    if (isFirstSearchRender.current) { isFirstSearchRender.current = false; return; }
    const t = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearch(searchTerm);
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ── Clear cache on filter/sort change ────────────────────────────
  useEffect(() => {
    if (isFirstFilterRender.current) { isFirstFilterRender.current = false; return; }
    setCurrentPage(1);
    prefetchCache.current.clear();
    prefetchInProgress.current.clear();
  }, [filters, sortField, sortOrder]);

  const fetchPricingHistory = async () => {
    try {
      const response = await apiHelper("sales/pricing-history/", "GET");

      if (!response.ok) {
        throw new Error("Failed to fetch pricing history");
      }

      const data = await response.json();

      console.log("Pricing History:", data);

      // Optional — map API → UI format
      const mappedHistory = data.map(mapPricingHistoryApiToUi);
      console.log("Mapped Pricing History:", mappedHistory);

      setPricingHistory(mappedHistory);

    } catch (error) {
      console.error(error);
      alert("Unable to load pricing history");
    }
  };

  useEffect(() => {
    fetchPricingHistory();
  }, []);

  useEffect(() => {
    console.log("LISTINGS FROM API", listings);
  }, [listings]);

  // --- Bulk Pricing Handlers ---
  useEffect(() => {
    const pages = Math.max(1, Math.ceil(bulkItems.length / BULK_PAGE_SIZE));
    if (bulkPage > pages) setBulkPage(1);
  }, [bulkItems, bulkPage]);

  const handleAddToBulkPricing = () => {
    if (selectedIds.length === 0) return;
    setBulkActive(true);
    const newOnes = selectedIds.filter((id) => !bulkItems.includes(id));
    const newBulkItems = [...bulkItems, ...newOnes];
    setBulkItems(newBulkItems);
    setSelectedIds(newBulkItems); // sync checkboxes to full bulk set
    setBulkDropdownOpen(true);
    const toolbarEl = bulkToolbarRef.current;
    if (toolbarEl)
      toolbarEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const removeBulkItem = (id) => {
    setBulkItems((prev) => {
      const next = prev.filter((x) => x !== id);
      if (next.length === 0) {
        setBulkDropdownOpen(false);
        setBulkActive(false);
      }
      return next;
    });
    setSelectedIds((prev) => prev.filter((x) => x !== id));
  };

  // --- Row / Drawer Handlers ---
  const onRowClick = (row) => {
    setSelectedRow(row);
    setDrawerTab("entity-info");
    setDrawerOpen(false);
  };

  const handleNotificationClose = () => {
    setNotification(null);
  };

  useEffect(() => {
    if (location.state?.notification) {
      setNotification(location.state.notification);

      // 🔥 clear router state so it shows only once
      history.replace({
        ...location,
        state: {},
      });
    }
  }, []);

  useEffect(() => {
    function handleClickOutsideDrawer(event) {
      if (
        drawerOpen &&
        drawerRef.current &&
        !drawerRef.current.contains(event.target)
      ) {
        setDrawerOpen(false);
        setSelectedRow(null);
        setSelectedIds((ids) =>
          selectedRow ? ids.filter((id) => id !== selectedRow.id) : ids,
        );
      }
    }
    document.addEventListener("mousedown", handleClickOutsideDrawer);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideDrawer);
  }, [drawerOpen, selectedRow]);


  useEffect(() => {
    function handleClickOutside(e) {
      if (
        dropdownOpenId !== null &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !buttonRefs.current[dropdownOpenId]?.contains(e.target)
      ) {
        setDropdownOpenId(null);
      }
    }
    if (dropdownOpenId !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownOpenId]);

  // --- Bulk Dropdown Outside Click ---
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        bulkToolbarRef.current &&
        !bulkToolbarRef.current.contains(e.target)
      ) {
        setBulkDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // --- Toolbar Dropdown Outside Click ---
  const toggleToolbarDropdown = () => setToolbarDropdownOpen((prev) => !prev);
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        setToolbarDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  //Archieve modal start here
  const archivedRef = useRef(null);
  const [archivedModalOpen, setArchivedModalOpen] = useState(false);
  const handleArchivedConfirm = () => {
    setArchivedModalOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (archivedRef.current && !archivedRef.current.contains(event.target)) {
        setArchivedModalOpen(false);
      }
    }

    if (archivedModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [archivedModalOpen]);

  //Save Modal start here
  const saveRef = useRef(null);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const handleSaveConfirm = () => {
    setSaveModalOpen(false);
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (saveRef.current && !saveRef.current.contains(event.target)) {
        setSaveModalOpen(false);
      }
    }

    if (saveModalOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [saveModalOpen]);

  // ph-prefixed state to avoid collisions
  const [phSearchTerm, setPhSearchTerm] = useState("");
  const [phSearchInput, setPhSearchInput] = useState("");
  const [phCurrentPage, setPhCurrentPage] = useState(1);
  const [phSelectedRow, setPhSelectedRow] = useState(null);
  const [phDrawerOpen, setPhDrawerOpen] = useState(false);
  const [phDrawerTab, setPhDrawerTab] = useState("overview");
  const phDrawerRef = useRef(null);

  const phFiltered = useMemo(() => {
    console.log(pricingHistory);
    let rows = [...(pricingHistory || [])];

    // Split search term into individual keywords (same pattern as reference)
    const terms = (phSearchTerm || "")
      .toLowerCase()
      .trim()
      .split(/[,\s]+/)
      .filter(Boolean);

    console.log(phSearchTerm + '------');
    console.log(rows);

    if (terms.length > 0) {
      rows = rows
        .map((r) => {
          // Create searchable text for header (initiator + reason) - similar to reference's searchableText
          const headerSearchableText = `
          ${r.initiator?.brandName ?? ""}
          ${r.reason ?? ""}
        `.toLowerCase();

          // Check if any keyword matches in header (OR condition - same as reference)
          const headerMatch = terms.some((word) => headerSearchableText.includes(word));

          // if header matches → keep entire history
          if (headerMatch) return r;

          // otherwise filter items only (search in stock_name and variant)
          const filteredChanges = r.changes?.filter((c) => {
            const itemSearchableText = `
            ${c.stockName ?? ""}
            ${c.variant ?? ""}
            ${c.condition ?? ""}
            ${c.grading ?? ""}
            ${c.listing_code ?? ""}
          `.toLowerCase();

            // Check if any keyword matches in item (OR condition - same as reference)
            return terms.some((word) => itemSearchableText.includes(word));
          });

          if (filteredChanges?.length) {
            return { ...r, changes: filteredChanges };
          }

          return null;
        })
        .filter(Boolean);
    }

    if (
      phAppliedFilter?.changesTime_start ||
      phAppliedFilter?.changesTime_end
    ) {
      const start = phAppliedFilter.changesTime_start
        ? new Date(phAppliedFilter.changesTime_start).setHours(0, 0, 0, 0)
        : null;

      const end = phAppliedFilter.changesTime_end
        ? new Date(phAppliedFilter.changesTime_end).setHours(23, 59, 59, 999)
        : null;

      rows = rows.filter((r) => {
        const rowTime = new Date(r.time).getTime();

        if (start && rowTime < start) return false;
        if (end && rowTime > end) return false;

        return true;
      });
    }
    console.log(rows);
    return rows;
  }, [pricingHistory, phSearchTerm, phAppliedFilter]);


  const flattenedPriceHistory = useMemo(() => {
    return phFiltered.flatMap((history) =>
      history.changes.map((change) => ({
        historyId: history.id,
        time: history.time,
        initiator: history.initiator,
        reason: history.reason,
        condition: history.condition,
        grading: history.grading,
        changeCount: history.changeCount,
        ...change,
      }))
    );
  }, [phFiltered]);
  const phTotalPages = Math.max(
    1,
    Math.ceil(flattenedPriceHistory.length / PH_PAGE_SIZE)
  );
  const phPageData = useMemo(() => {
    const start = (phCurrentPage - 1) * PH_PAGE_SIZE;
    const end = phCurrentPage * PH_PAGE_SIZE;

    return flattenedPriceHistory.slice(start, end);
  }, [flattenedPriceHistory, phCurrentPage]);
  // close drawer on outside click (same pattern as your archived tab)

  useEffect(() => {
    setPhCurrentPage(1);
  }, [flattenedPriceHistory.length]);
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        phDrawerOpen &&
        phDrawerRef.current &&
        !phDrawerRef.current.contains(e.target)
      ) {
        setPhDrawerOpen(false);
        setPhSelectedRow(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [phDrawerOpen]);


  /** tiny placeholder if noImage isn’t defined */
  const safeNoImage =
    "https://rukminim2.flixcart.com/image/704/844/xif0q/mobile/3/5/l/-original-imaghx9qmgqsk9s4.jpeg?q=90&crop=false";

  /** PricePill */
  const PricePill = ({ value, variant = "default" }) => (
    <span
      style={{
        borderRadius: 99,
        padding: "3px 10px",
        fontSize: 14,
        lineHeight: "15px",
        border: `1px solid ${variant === "danger" ? "#842029" : "#6C757D"}`,
        display: "inline-block",
        minWidth: 90,
        textAlign: "center",
        fontWeight: 600,
        background: variant === "danger" ? "#F8D7DA" : "#ffffff",
        color: variant === "danger" ? "#842029" : "#6C757D",
      }}
    >
      {value}
    </span>
  );

  /** Arrow */
  const Arrow = (props) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor" // <── use currentColor here
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );

  /** PlatformList */
  const PlatformList = ({ platforms = [] }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {platforms.map((p, i) => {
        const isDanger = p.color.toLowerCase() === "#842029";
        return (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
              <circle cx="4" cy="4" r="3" fill={p.color} />
            </svg>
            <span style={isDanger ? { color: p.color } : {}}>{p.name}</span>
            <span
              style={{
                marginLeft: "auto",
                ...(isDanger ? { color: p.color } : {}),
              }}
            >
              {p.price}
            </span>
          </div>
        );
      })}
    </div>
  );

  /** ErpList */
  const ErpList = ({ erp = [] }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {erp.map((e, i) => {
        const isDanger = e.color.toLowerCase() === "#842029";
        return (
          <div
            key={i}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <span
              style={{
                marginLeft: "auto",
                ...(isDanger ? { color: e.color } : {}),
              }}
            >
              {e.current}
            </span>
            <Arrow style={{ color: isDanger ? e.color : undefined }} />
            <PricePill
              value={e.target}
              variant={isDanger ? "danger" : "default"}
            />
          </div>
        );
      })}
    </div>
  );

  /** sample data (matches figma) */
  const sampleRows = [
    {
      id: 1,
      avatar: safeNoImage,
      name: "OnePlus Buds Pro 3",
      variant: "Sapphire Blue",
      condition: "New",
      grading: "Unactivated",
      baseCurrent: "SGD 0.00",
      baseTarget: "SGD 178.00",
      platforms: [
        { name: "Shopee SG", color: "#842029", price: "SGD 0.00" },
        { name: "Lazada SG", color: "#198754", price: "SGD 203.00" },
        { name: "TikTok SG", color: "#198754", price: "SGD 178.00" },
      ],
      erp: [
        {
          name: "Shopee SG",
          color: "#842029",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "Lazada SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "TikTok SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
      ],
    },
    {
      id: 2,
      avatar: safeNoImage,
      name: "OnePlus Buds Pro 3",
      variant: "Midnight Opus",
      condition: "New",
      grading: "Unactivated",
      baseCurrent: "SGD 0.00",
      baseTarget: "SGD 178.00",
      platforms: [
        { name: "Shopee SG", color: "#198754", price: "SGD 0.00" },
        { name: "Lazada SG", color: "#198754", price: "SGD 203.00" },
        { name: "TikTok SG", color: "#198754", price: "SGD 178.00" },
      ],
      erp: [
        {
          name: "Shopee SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "Lazada SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "TikTok SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
      ],
    },
    {
      id: 3,
      avatar: safeNoImage,
      name: "OnePlus Buds Pro 3",
      variant: "Lunar Radiance",
      condition: "New",
      grading: "Unactivated",
      baseCurrent: "SGD 0.00",
      baseTarget: "SGD 178.00",
      platforms: [
        { name: "Shopee SG", color: "#198754", price: "SGD 0.00" },
        { name: "Lazada SG", color: "#198754", price: "SGD 203.00" },
        { name: "TikTok SG", color: "#198754", price: "SGD 178.00" },
      ],
      erp: [
        {
          name: "Shopee SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "Lazada SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
        {
          name: "TikTok SG",
          color: "#198754",
          current: "SGD 0.00",
          target: "SGD 178.00",
        },
      ],
    },
  ];


  const FilterActiveIcon = () => (
    <svg
      style={{ margin: 0 }}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.6325 6.44444C20.7252 6.24267 20.7746 6.02858 20.8107 5.74756C21.0354 4.00089 21.1478 3.12756 21.6356 2.56378C20.1234 2 19.2177 2 17.4063 2H4.59369C2.78227 2 1.87655 2 1.36439 2.56378C0.852232 3.12756 0.964572 4.00089 1.18925 5.74756C1.25487 6.25766 1.36399 6.54725 1.69704 6.94296C2.77293 8.22128 4.74334 10.4951 7.50829 12.5623C7.76095 12.7512 7.92676 13.0599 7.95475 13.4016C8.26623 17.2071 8.55159 19.8068 8.70309 21.0865C8.78256 21.7577 9.52673 22.274 10.14 21.8404C11.1702 21.1118 13.0947 20.2952 13.349 18.938C13.4692 18.2964 13.6451 17.1765 13.8437 15.3333"
        stroke="#0D6EFD"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <g clipPath="url(#clip0_4516_4830)">
        <path
          d="M22.8125 11C22.8125 14.2102 20.2102 16.8125 17 16.8125C13.7898 16.8125 11.1875 14.2102 11.1875 11C11.1875 7.78987 13.7898 5.18753 17 5.18753C20.2102 5.18753 22.8125 7.78987 22.8125 11ZM16.3277 14.0777L20.6402 9.7652C20.7866 9.61877 20.7866 9.38132 20.6402 9.23488L20.1099 8.70456C19.9634 9.5581 19.726 9.5581 19.5795 8.70456L16.0625 12.2215L14.4205 10.5795C14.2741 10.4331 14.0366 10.4331 13.8901 10.5795L13.3598 11.1099C13.2134 11.2563 13.2134 11.4937 13.3598 11.6402L15.7973 14.0777C15.9438 14.2241 16.1812 14.2241 16.3277 14.0777Z"
          fill="#0D6EFD"
        />
      </g>
      <defs>
        <clipPath id="clip0_4516_4830">
          <rect
            width="12"
            height="12"
            fill="white"
            transform="translate(11 5)"
          />
        </clipPath>
      </defs>
    </svg>
  );

  const FilterInactiveIcon = () => (
    <svg
      style={{ margin: 0 }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M21.6323 6.44444C21.7251 6.24267 21.7744 6.02858 21.8106 5.74756C22.0353 4.00089 22.1476 3.12756 21.6354 2.56378C21.1233 2 20.2176 2 18.4062 2H5.59366C3.78225 2 2.87655 2 2.36439 2.56378C1.85223 3.12756 1.96457 4.00089 2.18925 5.74756C2.25487 6.25766 2.36399 6.54725 2.69703 6.94296C3.77291 8.22128 5.74331 10.4951 8.50823 12.5623C8.76089 12.7512 8.9267 13.0599 8.95469 13.4016C9.26616 17.2071 9.55152 19.8068 9.70302 21.0865C9.78249 21.7577 10.5267 22.274 11.1399 21.8404C12.1701 21.1118 14.0946 20.2952 14.3489 18.938C14.4691 18.2964 14.645 17.1765 14.8436 15.3333"
        stroke="#6C757D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M18.1113 7.5556V15.3334M22.0001 11.4445L14.2224 11.4445"
        stroke="#6C757D"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const filtersApplied = Object.entries(filters || {}).some(([key, value]) => {
    if(Array.isArray(value)) {
      return value.length > 0;
    }
    return value !== null && value !== undefined && value !== "";
  });

  const isInitialTableEmpty = listings?.length === 0;
  const isPhInitialTableEmpty = pricingHistory?.length === 0;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const sections = [
    {
      label: "Platforms",
      type: "list",
      key: "platforms",
      data: ["Shopee SG", "Lazada SG", "TikTok SG"],
      showDropdownIcon: true,
      renderItem: (name) => ({
        id: name, 
        title: name, 
        subtitle: "",
        image: null,
      }),
    },
    {
      label: "Platform Price",
      type: "range",
      key: "platformPrice",
      data: []
    },
    {
      label: "ERP Price",
      type: "range",
      key: "erpPrice",
      data: []
    },
  ];

  const phSections = [
    {
      label: "Changes Time",
      key: "changeTime",
      type: "dateRange",
    },
  ];

  const handleCopy = async (textToCopy) => {
    if (!textToCopy) {
      console.error("No text to copy");
      // alert('Failed to copy: No text available.');
      return;
    }

    // Use Clipboard API if available and secure
    if (
      window.isSecureContext &&
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function"
    ) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
      } catch (err) {
        console.error("Clipboard API failed:", err);
        fallbackCopyTextToClipboard(textToCopy);
      }
    } else {
      console.warn(
        "Clipboard API not available or not in secure context, using fallback",
      );
      fallbackCopyTextToClipboard(textToCopy);
    }
  };
  const fallbackCopyTextToClipboard = (text) => {
    try {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } else {
        console.error("Fallback copy failed");
        alert("Failed to copy text. Please copy manually.");
      }
    } catch (err) {
      console.error("Fallback copy error:", err);
      alert("Failed to copy text. Please copy manually.");
    }
  };
  return (
    <div className="company-body new-look stock-wrap bg-checkbox">
      {/* Breadcrumbs */}
      <div className="breadcrumb">
        {breadcrumbs.map((crumb, index) => (
          <span key={index} className="breadcrumb-item">
            {crumb}
            {index !== breadcrumbs.length - 1}
          </span>
        ))}
      </div>

      <div className="employee-tabs-container">
        {/* ─── Tab Buttons ─── */}
        <div className="tabs">
          <button
            className={`tab ${activeTab === "initiate-pricing" ? "active" : ""
              }`}
            onClick={() => setActiveTab("initiate-pricing")}
          >
            Initiate Pricing
          </button>

          <button
            className={`tab ${activeTab === "pricing-history" ? "active" : ""}`}
            onClick={() => setActiveTab("pricing-history")}
          >
            Pricing History
          </button>
        </div>

        {/* ─── Tab Contents ─── */}
        {activeTab === "initiate-pricing" && (
          <div className="tab-content" id="listing-content">
            {/* Stock List Card */}
            <div className="info-section">
              <div className="info-card" style={{ overflow: "unset" }}>
                <div className="entities-tab">
                  {/* Toolbar: Search + Filter on left; Edit/Plus on right */}
                  <div className="toolbar">
                    <div className="toolbar-left">
                      <SearchBox
                        searchInput={searchInput}
                        setSearchInput={setSearchInput}
                        setSearchTerm={setSearchTerm}
                        setCurrentPage={setCurrentPage}
                        disabled={isInitialTableEmpty}
                      />

                      <div style={{ position: "relative" }}>
                        <button
                          ref={filterButtonRef}
                          className={`icon-btn p-2 filter-btn ${filtersApplied ? "applied" : ""
                            }`}
                          onClick={() =>
                            !isInitialTableEmpty &&
                            setFilterPopupOpen(!filterPopupOpen)
                          }
                          disabled={isInitialTableEmpty}
                          style={{
                            border: filtersApplied
                              ? "1px solid #2C7BE5"
                              : "1px solid #6C757D",
                            borderWidth: "1px",
                            color: filtersApplied ? "#2C7BE5" : "#6C757D",
                            background: isInitialTableEmpty
                              ? "#E9ECEF" // disabled background
                              : filtersApplied  
                                ? "#CFE2FF" // active filter
                                : "#FFFFFF", // normal

                            height: "40px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",

                            opacity: isInitialTableEmpty ? 0.6 : 1,
                            pointerEvents: isInitialTableEmpty
                              ? "none"
                              : "auto", // block actions
                            cursor: isInitialTableEmpty
                              ? "not-allowed"
                              : "pointer",
                            borderRadius: "8px",
                          }}
                        >
                          {filtersApplied && !isInitialTableEmpty ? (
                            <FilterActiveIcon />
                          ) : (
                            <FilterInactiveIcon />
                          )}
                        </button>
                        <FilterDrawer
                          width={400}
                          isOpen={filterPopupOpen}
                          onClose={() => {
                            setTempValues({
                              platforms: [],
                              platformPrice_min: "",
                              platformPrice_max: "",
                              erpPrice_min: "",
                              erpPrice_max: "",
                            });
                            setFilterPopupOpen(false);
                          }}
                          sections={sections}
                          tempValues={tempValues}
                          setTempValues={setTempValues}
                          onApply={() => {
                            console.log("Applying filters with values:", tempValues);
                            setFilters({
                              platforms: tempValues?.platforms || [],
                              platformPrice_min: tempValues?.platformPrice_min || "",
                              platformPrice_max: tempValues?.platformPrice_max || "",
                              erpPrice_min: tempValues?.erpPrice_min || "",
                              erpPrice_max: tempValues?.erpPrice_max || "",
                            });
                            setFilterPopupOpen(false);
                          }}
                          onClear={() => {
                            const empty = {
                              platforms: [],
                              platformPrice_min: "",
                              platformPrice_max: "",
                              erpPrice_min: "",
                              erpPrice_max: "",
                            };
                            setTempValues(empty);
                            setFilters(empty);
                          }}
                          enableAutoSwap={true}
                        />
                      </div>
                    </div>

                    <div className="toolbar-right" ref={toolbarRef}>
                      {/* Flow-2 Start */}
                      {/* Bulk Pricing button with count (REPLACEMENT) */}
                      <div
                        style={{ position: "relative", border: "none" }}
                        ref={bulkToolbarRef}
                      >
                        <button
                          onClick={() => isInitialTableEmpty ? setBulkDropdownOpen(false) : setBulkDropdownOpen((prev) => !prev)}
                          style={{
                            position: "relative", // ✅ IMPORTANT
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 10px",
                            borderRadius: 8,
                            border: "none",
                            backgroundColor: !isInitialTableEmpty
                              ? "#212529"
                              : "#ced4da",
                            color: "#fff",
                            cursor: !isInitialTableEmpty
                              ? "pointer"
                              : "not-allowed",
                          }}
                        >
                          <PencilLine size={16} color="#fff" strokeWidth={2} />

                          <span
                            style={{
                              fontSize: 12,
                              fontWeight: 500,
                            }}
                          >
                            Bulk Pricing
                          </span>

                          {/* ✅ Count inside button (like screenshot) */}
                          {bulkItems.length > 0 && (
                            <span
                              style={{
                                minWidth: 18,
                                height: 18,
                                padding: "0 6px",
                                borderRadius: 999,
                                backgroundColor: "#fff",
                                color: "#212529",
                                fontSize: 11,
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              {bulkItems.length}
                            </span>
                          )}

                          <span
                            style={{
                              marginLeft: 4,
                              fontSize: 10,
                              transform: bulkDropdownOpen
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.15s ease",
                              lineHeight: 1,
                            }}
                          >
                            ▼
                          </span>
                        </button>

                        {bulkDropdownOpen && (
                          <div
                            style={{
                              position: "absolute",
                              right: 0,
                              top: "48px",
                              zIndex: 1500,
                              width: "566px",
                              border:
                                "1px solid var(--Utilised-line-1, #DEE2E6)",
                              borderRadius: "12px",
                              boxShadow: "0 4px 10px 0 rgba(0, 0, 0, 0.12)",
                            }}
                            className="bulk-dropdown-wrapper"
                          >
                            <div
                              className="bulk-dropdown"
                              onClick={(e) => e.stopPropagation()}
                              style={{padding: "20px",}}
                            >
                              {/* {bulkItems.length > 0 && ( */}
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: 5,
                                  paddingBottom: 15,
                                  // borderBottom: "1px solid #E9ECEF",
                                }}
                              >
                                <div
                                  style={{ fontSize: 14, color: "#212529" }}
                                >{`${bulkItems.length} Listings`}</div>
                                <div
                                  style={{
                                    display: "flex",
                                    gap: 10,
                                    alignItems: "center",
                                  }}
                                >
                                  <div
                                    className="bulk-clear-link"
                                    onClick={() => {
                                      if (bulkItems.length === 0) return;
                                      clearSelection();
                                    }}
                                    style={{
                                      cursor:
                                        bulkItems.length > 0
                                          ? "pointer"
                                          : "not-allowed",
                                      color:
                                        bulkItems.length > 0
                                          ? "#212529"
                                          : "#adb5bd",
                                      pointerEvents:
                                        bulkItems.length > 0 ? "auto" : "none", // ✅ hard disable
                                    }}
                                  >
                                    Clear
                                  </div>
                                  <button
                                    className="icon-btn"
                                    disabled={bulkItems.length === 0} // ✅ real disable
                                    style={{
                                      cursor:
                                        bulkItems.length > 0
                                          ? "pointer"
                                          : "not-allowed",
                                      backgroundColor:
                                        bulkItems.length > 0
                                          ? "#212529"
                                          : "#E9ECEF",
                                      border:
                                        "1px solid var(--Utilised-neutral-600, #ADB5BD)",
                                      color:
                                        bulkItems.length > 0
                                          ? "#FFF"
                                          : "#ADB5BD",
                                    }}
                                    onClick={() => {
                                      if (bulkItems.length === 0) return; // safety guard

                                      const resolvedBulkItems = [...bulkItems]
                                        .reverse()
                                        .map((id) =>
                                          listings.find((d) => d.id === id),
                                        )
                                        .filter(Boolean);

                                      console.log("Resolved bulk items for pricing:", resolvedBulkItems);

                                      clearSelection();
                                      history.push(
                                        "/platform-pricing/bulk-pricing",
                                        {
                                          bulkItems: resolvedBulkItems, // full objects
                                        },
                                      );
                                    }}
                                  >
                                    Start Pricing
                                  </button>
                                </div>
                              </div>
                              {/* )} */}

                              {(() => {
                                // Descending by add-time: most recently added first.
                                // bulkItems is append-ordered (oldest at index 0),
                                // so reversing gives newest-first before paginating.
                                const bulkItemsDesc = [...bulkItems].reverse();
                                // const bulkPageData = bulkItemsDesc.slice(
                                //   (bulkPage - 1) * BULK_PAGE_SIZE,
                                //   bulkPage * BULK_PAGE_SIZE,
                                // );

                                const sortedBulkItems = bulkItemsDesc
                                  .map((id) =>
                                    listings.find((d) => d.id === id),
                                  )
                                  .filter(Boolean);

                                return (
                                  <>
                                    {sortedBulkItems.length === 0 ? (
                                      /* ✅ EMPTY STATE */
                                      <div
                                        style={{
                                          padding: "16px",
                                          textAlign: "center",
                                          color: "#6C757D",
                                          fontSize: 13,
                                        }}
                                      >
                                        {/* No data */}
                                        <div className="empty-state">
                                          <div className="empty-state-content">
                                            <div class="icon">
                                              <img
                                                src={noBrandImage}
                                                alt="avatar"
                                                className="avatar"
                                                style={{
                                                  width: 120,
                                                  height: 120,
                                                  verticalAlign: "middle",
                                                }}
                                              />
                                            </div>
                                            <h1 class="no-item-title mt-2" style={{fontFamily: "Montserrat", fontSize: "14px", fontWeight: 600, color: "#212529", marginTop: 10, marginBottom: 10}}>
                                              No Listings Added
                                            </h1>
                                            <p class="no-item-text" style={{color: "#6C757D", fontFamily: "Montserrat", fontSize: '12px', fontWeight: 400}}>
                                              Select Listings and click "Add to
                                              Pricing" to start
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <>
                                        <div
                                          style={{
                                            maxHeight: "800px",
                                            overflowY: "auto",
                                            minHeight: 0,
                                          }}
                                        >
                                          {sortedBulkItems.map((opt, index) => (
                                            <div
                                              key={opt.id}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                padding: "10px 12px",
                                                borderBottom:
                                                  "1px solid #E9ECEF",
                                                backgroundColor:
                                                  index % 2 === 0
                                                    ? "#FFFFFF"
                                                    : "#F8F9FA", // ✅ zebra rows
                                              }}
                                            >
                                              {/* Image */}
                                              <img
                                                src={
                                                  opt.avatar ||
                                                  "https://via.placeholder.com/36"
                                                }
                                                alt={opt.name}
                                                style={{
                                                  width: 36,
                                                  height: 36,
                                                  marginRight: 10,
                                                }}
                                              />

                                              {/* Content */}
                                              <div style={{ flex: 1 }}>
                                                <div
                                                  style={{
                                                    fontSize: 13,
                                                    fontWeight: 500,
                                                    color: "#212529",
                                                  }}
                                                >
                                                  {opt.name}
                                                </div>

                                                <div
                                                  style={{
                                                    fontSize: 11,
                                                    color: "#6C757D",
                                                    marginTop: 2,
                                                  }}
                                                >
                                                  {String(opt.variant || "")}
                                                </div>

                                                <div
                                                  style={{
                                                    display: "flex",
                                                    gap: 6,
                                                    marginTop: 4,
                                                    alignItems: "center",
                                                  }}
                                                >
                                                  {/* Condition pill */}
                                                  <span
                                                    className="badge-condition"
                                                    style={{
                                                      padding: "2px 5px",
                                                    }}
                                                  >
                                                    {opt.condition || "New"}
                                                  </span>

                                                  {/* Grading pill */}
                                                  <span
                                                    className="badge-grading"
                                                    style={{
                                                      padding: "2px 5px",
                                                    }}
                                                  >
                                                    {opt.grading ||
                                                      "Not Activated"}
                                                  </span>
                                                </div>
                                              </div>

                                              {/* Remove button */}
                                              <button
                                                onClick={() =>
                                                  removeBulkItem(opt.id)
                                                }
                                                style={{
                                                  border: "none",
                                                  background: "transparent",
                                                  cursor: "pointer",
                                                  padding: 4,
                                                  marginLeft: 8,
                                                }}
                                                aria-label={`Remove ${opt.name}`}
                                              >
                                                <X size={16} />
                                              </button>
                                            </div>
                                          ))}
                                        </div>
                                      </>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  <div
                    className="table-wrapper"
                    style={{
                     border: "none",
                    }}
                  >
                    {pageData.length === 0 && !debouncedSearch ? (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <div class="icon">
                            <img
                              src={noBrandImage}
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 120,
                                height: 120,
                                verticalAlign: "middle",
                              }}
                            />
                          </div>
                          <h1 class="no-item-title mt-2">
                            No Listings Found for Pricing
                          </h1>
                          <p class="no-item-text">
                            Add a Listing first to initiate Pricing
                          </p>
                        </div>
                      </div>
                    ) : totalCount === 0 && debouncedSearch ?  (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <div class="icon">
                            <img
                              src={emptySearchImg}
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 120,
                                height: 120,
                                verticalAlign: "middle",
                              }}
                            />
                          </div>
                          <h1 class="no-item-title mt-2">
                            No Listings Found for Pricing
                          </h1>
                          <p class="no-item-text">
                            No results match what you're looking for
                          </p>
                        </div>
                      </div>
                    ) : (
                      <table className="entities-table" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                        <thead>
                          <tr>
                            <th className="sticky-action-col" style={{ position: "sticky", left: 0, zIndex: 2, }}>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={
                                    pageData.length > 0 &&
                                    pageData.every((d) =>
                                      selectedIds.includes(d.id),
                                    )
                                  }
                                  onChange={toggleAll}
                                />
                              </div>
                            </th>
                            <SortableHeader
                              label="Listing"
                              field="name"
                              sortField={sortField}
                              sortOrder={sortOrder}
                              handleSort={handleSort}
                              style={{ position: "sticky", left: 40, zIndex: 2 }}
                            />
                            <th>
                              <span>Platforms</span>
                            </th>
                            <th>
                              <span>Platform Price</span>
                            </th>

                            <th>
                              <span>ERP Price</span>
                            </th>
                            <th>
                              <span>Last Sync Time</span>
                            </th>
                            <SortableHeader
                              label="Last Modified Time"
                              field="last_modified_time"
                              sortField={sortField}
                              sortOrder={sortOrder}
                              handleSort={handleSort}
                            />
                          </tr>
                        </thead>

                        <tbody>
                          {pageData.map((row, idx) => {
                            const isRowSelected =
                              (selectedIds.includes(row.id) ||
                                dropdownOpenId === row.id) &&
                              !suppressHighlight;

                            return (
                              <tr
                                key={row.id}
                                className={[
                                  idx % 2 ? "striped" : "",
                                  isRowSelected ? "row-selected" : "",
                                ].join(" ")}
                                onClick={() => onRowClick(row)}
                              >
                                {/* Checkbox cell */}
                                <td
                                  onClick={(e) => e.stopPropagation()}
                                  className="sticky-action-col"
                                  style={{ position: "sticky", left: 0, zIndex: 2, backgroundColor: idx % 2 ? "#f8f9fa" : "#fff" }}
                                >
                                  <div className="form-check">
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={selectedIds.includes(row.id)}
                                      onChange={(e) => {
                                        toggleSelect(row.id);
                                        if (e.target.checked) {
                                          setSelectedRow(row);
                                        } else if (selectedRow?.id === row.id) {
                                          setSelectedRow(null);
                                        }
                                      }}
                                    />
                                  </div>
                                </td>

                                {/* Stock */}
                                <td style={{ position: "sticky", left: 40, zIndex: 2, backgroundColor: idx % 2 ? "#f8f9fa" : "#fff" }}>
                                  <div
                                    className="name-cell"
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 10,
                                    }}
                                  >
                                    <img
                                      src={row.avatar || noImage}
                                      alt="avatar"
                                      className="avatar"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        marginRight: 8,
                                        verticalAlign: "middle",
                                      }}
                                    />
                                    <div>
                                      <div>{row.name}</div>
                                      <div className="sub-line mt-1">
                                        {row.variant}
                                      </div>
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 5,
                                          marginTop: 5,
                                        }}
                                      >
                                        {/* small badges like New / Activated */}
                                        <span
                                          className=""
                                          style={{
                                            display: "flex",
                                            padding: "2px 5px",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "5px",
                                            borderRadius: "99px",
                                            border:
                                              "1px solid var(--Primary-Color, #FFEB00)",
                                            background:
                                              "var(--Primary-Color, #FFEB00)",
                                            color:
                                              "var(--Utilised-text-1, #212529)",
                                            textAlign: "center",
                                            fontFamily: "Montserrat",
                                            fontSize: "8px",
                                            fontStyle: "normal",
                                            fontWeight: 600,
                                            lineHeight: "normal",
                                          }}
                                        >
                                          {row.condition}
                                        </span>
                                        <span
                                          className="sub-line m-0"
                                          style={{
                                            display: "flex",
                                            padding: "2px 5px",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            gap: "5px",
                                            borderRadius: "99px",
                                            border:
                                              "1px solid var(--Primary-Color, #FFEB00)",
                                            background:
                                              "var(--Primary-Color, #FFEB00)",
                                            color:
                                              "var(--Utilised-text-1, #212529)",
                                            textAlign: "center",
                                            fontFamily: "Montserrat",
                                            fontSize: "8px",
                                            fontStyle: "normal",
                                            fontWeight: 600,
                                            lineHeight: "normal",
                                          }}
                                        >
                                          {row.grading}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                    }}
                                  >
                                    {row.platforms.map((p, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 8,
                                          fontSize: 12,
                                          color: p.textColor,
                                        }}
                                      >
                                        {/* dot */}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}>
                                          <circle cx="4" cy="4" r="3" fill={p.dotColor} />
                                        </svg>
                                        <span>{p.name}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                    }}
                                  >
                                    {row.platforms.map((p, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 12,
                                          color: p.textColor,
                                        }}
                                      >
                                        {p.platformPrice}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                    }}
                                  >
                                    {row.platforms.map((p, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          fontSize: 12,
                                          color: p.textColor,
                                        }}
                                      >
                                        {p.erpPrice}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                      fontSize: 12,
                                    }}
                                  >
                                    {row.platforms.map((p, i) => (
                                      <div
                                        key={i}
                                        style={{
                                          color: p.textColor,
                                        }}
                                      >
                                        {p.lastSync}
                                      </div>
                                    ))}
                                  </div>
                                </td>
                                <td>
                                  <div
                                    style={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 6,
                                      fontSize: 12,
                                    }}
                                  >
                                    {row.last_modified_time}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                    {/* Per‐row dropdown menu */}
                    {dropdownOpenId !== null && (
                      <div
                        ref={dropdownRef}
                        className="backdrop-cls"
                        style={{
                          position: "absolute",
                          top: dropdownPosition.top,
                          left: dropdownPosition.left,
                          background: "white",
                          boxShadow: "0px 1px 4px 0px #0000001A",
                          zIndex: 1000,
                          minWidth: 152,
                          borderRadius: 8,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="btn-align-forall">
                          <div className="common-for-all">
                            <button
                              onClick={() => {
                                alert("View Details clicked");
                                setDropdownOpenId(null); // 👈 Close dropdown
                              }}
                            >
                              View Details
                            </button>
                          </div>

                          <div className="common-for-all">
                            <button
                              // onClick={() => {
                              //     setSaveModalOpen(true);
                              //     setDropdownOpenId(null); // 👈 Close dropdown
                              // }}
                              onClick={() =>
                                history.push("/listing-pricing/edit-listing")
                              }
                            >
                              Edit
                            </button>
                          </div>

                          {/* Active / Deactivate */}
                          {/* ✅ Active / Deactivate */}
                          <div className="common-for-all">
                            {selectedRow.status === "Active" ? (
                              <button
                                onClick={() => {
                                  setSelectedStockId(selectedRow.id);
                                  setDeactivateModalOpen(true);
                                  setDropdownOpenId(null);
                                }}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  handleDeactivateStock(selectedRow.id);
                                  setDropdownOpenId(null);
                                }}
                              >
                                Activate
                              </button>
                            )}
                          </div>

                          <div className="common-for-all">
                            <button
                              onClick={() => {
                                // setArchivedModalOpen(true);
                                setDropdownOpenId(null); // 👈 Close dropdown
                              }}
                              className="deactivate-cls"
                            >
                              Archive
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {showDuplicateWarning && (
                      <div
                        className="d-flex align-items-center justify-content-between"
                        role="alert"
                        style={{
                          boxShadow: "0px 4px 10px 0px #0000001F",
                          padding: "15px 10px",
                          borderRadius: "12px",
                          border: "1px solid #FFC107",
                          backgroundColor: "#FFF3CD",
                          color: "#664D03",
                          fontSize: "12px",
                          position: "fixed",
                          top: "10px",
                          width: "calc(100% - 20px)",
                          maxWidth: "610px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          zIndex: 2000,
                        }}
                      >
                        <div className="d-flex align-items-center">
                          <Info className="me-2" />
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <strong>Duplicate entries weren’t added</strong> ——
                            All other items have been successfully added
                          </div>
                        </div>

                        <X
                          size={16}
                          style={{ cursor: "pointer" }}
                          onClick={() => setShowDuplicateWarning(false)}
                        />
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalCount > 0 && (
                    <div className="pagination" style={{ marginTop: 16 }}>
                      <div className="pagination-info">
                        {`${(currentPage - 1) * PAGE_SIZE + 1} - ${Math.min(
                          currentPage * PAGE_SIZE,
                          totalCount,
                        )} of ${totalCount} items`}
                      </div>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                      />
                    </div>
                  )}

                  {selectedIds.length > 0 && (
                    <div className="bottom-selection-bar">
                      <div className="bulk-count-bubble" style={{ color: "#212529" }}>
                        <div>{selectedIds.length} Selected</div>
                        <button
                          onClick={clearSelection}
                          title="Clear selection"
                        >
                          <X size={18} />
                        </button>
                      </div>

                      <div style={{ paddingLeft: '100px' }}>
                        <button
                          onClick={handleAddToBulkPricing}
                          className="bulk-action-btn"
                          style={{
                            backgroundColor: "#212529",
                            color: "#fff",
                          }}
                        >
                          <Plus size={18} /> Add to Bulk Pricing
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ------------------ End Filter Drawer ------------------ */}

                  {/* Flow-2 End */}
                  {/* )} */}
                  {saveModalOpen && (
                    <>
                      <div className="drawer-overlay" />
                      <div
                        className="entity-drawer modal-popup-cls"
                        ref={saveRef}
                        style={{
                          maxWidth: 650,
                          margin: "0 auto",
                          background: "white",
                          borderRadius: 12,
                          zIndex: 1050,
                          padding: 24,
                          position: "fixed",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                          height: "auto",
                          right: "auto",
                          overflow: "inherit",
                        }}
                      >
                        <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                          <button
                            className="close-btn"
                            onClick={() => setSaveModalOpen(false)}
                          >
                            <X size={20} />
                          </button>
                        </div>
                        <h3
                          style={{
                            textAlign: "left",
                            color: "#212529",
                            fontSize: "20px",
                            fontWeight: "600",
                            lineHeight: "24px",
                            fontFamily: "Montserrat",
                            marginBottom: "5px",
                          }}
                        >
                          New feature in Progress
                        </h3>
                        <p
                          style={{
                            marginBottom: "0",
                            textAlign: "left",
                            color: "#6C757D",
                            fontSize: "14px",
                            fontWeight: "400",
                            lineHeight: "20px",
                            fontFamily: "Montserrat",
                          }}
                        >
                          Work in Progress
                        </p>
                        <div
                          className="drawer-footer"
                          style={{ paddingTop: "10px" }}
                        >
                          <button
                            className="btn-save"
                            onClick={handleSaveConfirm}
                          >
                            OK
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Single‐row Drawer (unchanged) */}
            {drawerOpen &&
              selectedRow &&
              (() => {
                const pendingCount =
                  selectedRow.pending?.reduce(
                    (sum, item) => sum + item.count,
                    0,
                  ) || 0;
                const total = Number(selectedRow.available || 0) + pendingCount;
                return (
                  <>
                    <div className="drawer-overlay" />
                    <div className="entity-drawer" ref={drawerRef}>
                      <div className="drawer-header">
                        <h3>Listing Info</h3>
                        <button
                          className="close-btn"
                          onClick={() => {
                            setDrawerOpen(false);
                            setSelectedIds((ids) =>
                              ids.filter((id) => id !== selectedRow?.id),
                            );
                            setSelectedRow(null);
                          }}
                        >
                          <X size={24} />
                        </button>
                      </div>
                      <div className="main-drawer-inner">
                        <div className="drawer-title d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center">
                            <img
                              src="https://rukminim2.flixcart.com/image/704/844/xif0q/mobile/3/5/l/-original-imaghx9qmgqsk9s4.jpeg?q=90&crop=false"
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 40,
                                height: 40,
                                marginRight: 8,
                                verticalAlign: "middle",
                              }}
                            />
                            <h2 className="pb-0">{selectedRow?.name}</h2>
                          </div>
                          <div className="entities-tab flex-row align-items-center gap-3">
                            <div class="main-company-body-inner">
                              <div class="edit-organisation-info d-flex justify-content-end align-items-center gap-2 p-0">
                                <button
                                  class="dark-border gray-btn"
                                  type="button"
                                  // onClick={() => setSaveModalOpen(true)}
                                  onClick={() =>
                                    history.push(
                                      "/listing-pricing/edit-listing",
                                    )
                                  }
                                >
                                  Edit
                                </button>
                              </div>
                            </div>
                            <div className="toolbar">
                              <div
                                className="toolbar-right"
                                style={{ position: "relative" }}
                                ref={toolbarRef}
                              >
                                <button
                                  className="drawer-dots"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleToolbarDropdown();
                                  }}
                                >
                                  <MoreHorizontal size={18} />
                                </button>

                                {toolbarDropdownOpen && (
                                  <div
                                    className="backdrop-cls"
                                    style={{
                                      position: "absolute",
                                      top: 40,
                                      right: 0,
                                      background: "white",
                                      boxShadow: "0px 1px 4px 0px #0000001A",
                                      zIndex: 1000,
                                      minWidth: 152,
                                      borderRadius: 8,
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <div className="btn-align-forall">
                                      <div className="common-for-all">
                                        <button
                                          onClick={() =>
                                            alert("Deactivate clicked")
                                          }
                                        >
                                          Deactivate
                                        </button>
                                      </div>
                                      <div className="common-for-all">
                                        <button className="deactivate-cls">
                                          Archive
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="drawer-tabs">
                          <button
                            className={
                              drawerTab === "entity-info" ? "active" : ""
                            }
                            onClick={() => setDrawerTab("entity-info")}
                          >
                            General
                          </button>
                          <button
                            className={drawerTab === "platform" ? "active" : ""}
                            onClick={() => setDrawerTab("platform")}
                          >
                            Platform
                          </button>
                          <button
                            className={
                              drawerTab === "additional-info" ? "active" : ""
                            }
                            onClick={() => setDrawerTab("additional-info")}
                          >
                            Additional Info
                          </button>
                        </div>

                        <div className="drawer-content">
                          {drawerTab === "entity-info" && (
                            <div className="tab-panel general-panel">
                              <div
                                className="card-section"
                                style={{
                                  borderRadius: "12px",
                                  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)", // subtle elevation
                                  overflow: "hidden",
                                  marginBottom: "20px",
                                  marginTop: "15px",
                                }}
                              >
                                <div
                                  className="card-header"
                                  style={{
                                    backgroundColor: " #F8F9FA",
                                    padding: "15px 20px",
                                    fontFamily: "Montserrat",
                                    fontSize: "14px",
                                    fontWeight: 600,
                                    color: " #212529",
                                    display: "flex",
                                    alignItems: "center",
                                    alignSelf: "stretch",
                                    borderRadius: "12px 12px 0px 0px",
                                    gap: "10px",
                                    height: "50px",
                                    borderBottom: "none",
                                  }}
                                >
                                  Basic Info
                                </div>
                                {/* BODY */}
                                <div
                                  className="card-body"
                                  style={{
                                    backgroundColor: "#FFFFFF",
                                    padding: "16px",
                                    display: "grid",
                                    gridTemplateColumns: "repeat(2, 1fr)",
                                    gap: "16px",
                                  }}
                                >
                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      display: "flex",
                                      flexDirection: "column",
                                      minHeight: "60px",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">SPU & SKU</div>
                                    <div className="field-value">
                                      <img
                                        src={selectedRow.avatar}
                                        alt="avatar"
                                        className="avatar"
                                        style={{
                                          width: 40,
                                          height: 40,
                                          marginRight: 8,
                                          verticalAlign: "middle",
                                        }}
                                      />
                                      <a
                                        href="#"
                                        onClick={() => setSaveModalOpen(true)}
                                        rel="noopener noreferrer"
                                        className="d-flex align-items-center gap-1 me-3"
                                        style={{
                                          color: "#2C7BE5",
                                          textDecoration: "underline",
                                          fontWeight: "600",
                                          fontSize: "12px",
                                          marginBottom: "10px",
                                        }}
                                      >
                                        {selectedRow.name}
                                        <SquareArrowOutUpRight
                                          size={14}
                                          strokeWidth={3}
                                        />
                                      </a>
                                    </div>
                                    <div
                                      className="field-value"
                                      style={{
                                        marginBottom: "2px",
                                        gap: "4px",
                                      }}
                                    >
                                      {selectedRow.condition}
                                    </div>
                                    <div
                                      className="field-value"
                                      style={{ gap: "4px" }}
                                    >
                                      <span
                                        className="status active"
                                        style={{
                                          minWidth: "unset",
                                          padding: "2px 10px",
                                          lineHeight: "normal",
                                        }}
                                      >
                                        {selectedRow.grading}
                                      </span>
                                    </div>
                                  </div>

                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      display: "flex",
                                      flexDirection: "column",
                                      minHeight: "60px",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">Status</div>
                                    <div className="field-value">
                                      <span
                                        className={`status ${String(
                                          selectedRow?.status || "",
                                        ).toLowerCase()}`}
                                      >
                                        {selectedRow?.status || (
                                          <span style={{ color: "#B0B0B0" }}>
                                            NA
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      gridColumn: "1 / -1", // ✅ span both columns
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "flex-start",
                                      // width: '100%',
                                      padding: "0",
                                    }}
                                  >
                                    <div
                                      className="field-label"
                                      style={{ marginBottom: "4px" }}
                                    >
                                      ID
                                    </div>
                                    <div
                                      className="field-value"
                                      style={{
                                        width: "100%",
                                        wordBreak: "break-all",
                                      }}
                                    >
                                      {selectedRow.model_id || (
                                        <span style={{ color: "#B0B0B0" }}>
                                          NA
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      padding: "0",
                                      gridColumn: "1 / -1",
                                      display: "flex",
                                      flexDirection: "column",
                                      width: "100%",
                                    }}
                                  >
                                    <div
                                      className="field-label"
                                      style={{ marginBottom: "4px" }}
                                    >
                                      Open ID
                                    </div>

                                    <div className="field-value cell-wrapper">
                                      {selectedRow.uuid ? (
                                        <div className="value-container">
                                          <span
                                            className="value-clickable"
                                            onClick={() =>
                                              handleCopy(selectedRow.uuid)
                                            }
                                          >
                                            {selectedRow.uuid}
                                          </span>
                                          <Copy
                                            size={13}
                                            className="copy-icon"
                                            onClick={() =>
                                              handleCopy(selectedRow.uuid)
                                            }
                                          />
                                          {copied && (
                                            <span className="copied-label">
                                              Copied!
                                            </span>
                                          )}
                                        </div>
                                      ) : (
                                        <span style={{ color: "#B0B0B0" }}>
                                          NA
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">
                                      Created By
                                    </div>
                                    <div className="field-value">
                                      {selectedRow.created_by || (
                                        <span style={{ color: "#B0B0B0" }}>
                                          NA
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">
                                      Created Time
                                    </div>
                                    <div className="field-value">
                                      {formatDateTime(
                                        selectedRow.created_at,
                                      ) || (
                                          <span style={{ color: "#B0B0B0" }}>
                                            NA
                                          </span>
                                        )}
                                    </div>
                                  </div>

                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">
                                      Last Modified By
                                    </div>
                                    <div className="field-value">
                                      {selectedRow.modified_by || (
                                        <span style={{ color: "#B0B0B0" }}>
                                          NA
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  <div
                                    className="field-block"
                                    style={{
                                      borderBottom: "none",
                                      padding: "0",
                                    }}
                                  >
                                    <div className="field-label">
                                      Last Modified Time
                                    </div>
                                    <div className="field-value">
                                      {formatDateTime(
                                        selectedRow.modified_at,
                                      ) || (
                                          <span style={{ color: "#B0B0B0" }}>
                                            NA
                                          </span>
                                        )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* ===== Product INFO CARD ===== */}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
            {deactivateModalOpen && (
              <ConfirmDeactivateModal
                isOpen={deactivateModalOpen}
                onCancel={() => setDeactivateModalOpen(false)}
                onConfirm={() => {
                  handleDeactivateStock(selectedStockId);
                  // setDeactivateModalOpen(false);
                }}
                entityName="Listing"
              />
            )}

            {notification && (
              <AlertNotification
                type={notification.type}
                recordName={notification.recordName}
                action={notification.action}
                message={notification.message}
                onClose={handleNotificationClose}
              />
            )}

            {archivedModalOpen && (
              <>
                {/* Overlay backdrop */}
                <div className="drawer-overlay" />

                <div
                  className="entity-drawer modal-popup-cls"
                  ref={archivedRef}
                  style={{
                    maxWidth: 650,
                    margin: "0 auto",
                    background: "white",
                    borderRadius: 12,
                    zIndex: 1050,
                    padding: 24,
                    position: "fixed",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.2)",
                    height: "auto",
                    right: "auto",
                    overflow: "inherit",
                  }}
                >
                  {/* Header */}
                  <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                    <button
                      className="close-btn"
                      onClick={() => setArchivedModalOpen(false)}
                    >
                      <X size={20} />
                    </button>
                  </div>

                  <h3
                    style={{
                      textAlign: "left",
                      color: "#212529",
                      fontSize: "20px",
                      fontWeight: "600",
                      lineHeight: "24px",
                      fontFamily: "Montserrat",
                      marginBottom: "5px",
                    }}
                  >
                    Branch can’t be archived
                  </h3>
                  {/* Subtext */}
                  <p
                    style={{
                      marginBottom: "0",
                      textAlign: "left",
                      color: "#6C757D",
                      fontSize: "14px",
                      fontWeight: "400",
                      lineHeight: "20px",
                      fontFamily: "Montserrat",
                    }}
                  >
                    Branch stocked with items can’t be archived, clear stock
                    before archiving. Archive action is also subject to approval
                  </p>

                  {/* Footer */}
                  <div className="drawer-footer" style={{ paddingTop: "10px" }}>
                    <button
                      className="btn-save"
                      onClick={handleArchivedConfirm}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === "pricing-history" && (
          <div className="tab-content" id="price-history-content">
            <div className="info-section">
              <div className="info-card">
                <div className="entities-tab">
                  {/* toolbar */}
                  <div className="toolbar">
                    <div className="toolbar-left">
                      <SearchBox
                        searchInput={phSearchInput}
                        setSearchInput={setPhSearchInput}
                        setSearchTerm={setPhSearchTerm}
                        setCurrentPage={setPhCurrentPage}
                        disabled={isPhInitialTableEmpty}
                      />
                      <button
                        className={`icon-btn p-2 filter-btn ${phAppliedFilter ? "applied" : ""
                          }`}
                        onClick={() =>
                          !isPhInitialTableEmpty &&
                          setPhFilterPopupOpen((prev) => !prev)
                        }
                        disabled={isPhInitialTableEmpty}
                        style={{
                          border: phAppliedFilter
                            ? "1px solid #2C7BE5"
                            : "1px solid #6C757D",

                          borderWidth: "1px",
                          color: phAppliedFilter ? "#2C7BE5" : "#6C757D",

                          background: isPhInitialTableEmpty
                            ? "#E9ECEF"
                            : phAppliedFilter
                              ? "#CFE2FF"
                              : "#FFFFFF",

                          height: "40px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",

                          opacity: isPhInitialTableEmpty ? 0.6 : 1,
                          pointerEvents: isPhInitialTableEmpty
                            ? "none"
                            : "auto",
                          cursor: isPhInitialTableEmpty
                            ? "not-allowed"
                            : "pointer",
                          borderRadius: "8px",
                        }}
                      >
                        {phAppliedFilter && !isPhInitialTableEmpty ? (
                          <FilterActiveIcon />
                        ) : (
                          <FilterInactiveIcon />
                        )}
                      </button>
                      <FilterDrawer
                        width={400}
                        isOpen={phFilterPopupOpen}
                        onClose={() => setPhFilterPopupOpen(false)}
                        sections={phSections}
                        tempValues={phTempValues}
                        setTempValues={setPhTempValues}
                        onApply={() => {
                          const hasRange =
                            phTempValues.changesTime_start ||
                            phTempValues.changesTime_end;

                          if (!hasRange) {
                            setPhAppliedFilter(null);
                          } else {
                            setPhAppliedFilter({
                              changesTime_start: phTempValues.changesTime_start,
                              changesTime_end: phTempValues.changesTime_end,
                            });
                          }

                          setPhCurrentPage(1); // pagination reset (important)
                          setPhFilterPopupOpen(false);
                        }}
                        onClear={() => {
                          setPhTempValues({
                            changesTime_start: null,
                            changesTime_end: null,
                          });
                          setPhAppliedFilter(null);
                        }}
                      />
                    </div>
                  </div>

                  {/* table */}
                  <div className="table-wrapper">
                    {pricingHistory.length === 0 ? (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <div class="icon">
                            <img
                              src={noBrandImage}
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 120,
                                height: 120,
                                verticalAlign: "middle",
                              }}
                            />
                          </div>
                          <h1 class="no-item-title mt-2">
                            No Listings Found for Pricing
                          </h1>
                          <p class="no-item-text">
                            Add a Listing first to initiate Pricing
                          </p>
                        </div>
                      </div>
                    ) : phFiltered.length === 0 &&
                      (phSearchTerm || phFiltered) ? (
                      <div className="empty-state">
                        <div className="empty-state-content">
                          <div class="icon">
                            <img
                              src={emptySearchImg}
                              alt="avatar"
                              className="avatar"
                              style={{
                                width: 120,
                                height: 120,
                                verticalAlign: "middle",
                              }}
                            />
                          </div>
                          <h1 class="no-item-title mt-2">No History Found</h1>
                          <p class="no-item-text">
                            No results match what you're looking for
                          </p>
                        </div>
                      </div>
                    ) : (
                      <table className="entities-table table table-striped">
                        <thead>
                          <tr>
                            <th>Changes Time</th>
                            <th>Initiator</th>
                            <th>Listing</th>
                            <th>Base Price Changes</th>
                            <th>Platforms</th>
                            <th>ERP Price Changes</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phPageData.map((row, idx) => (
                            <tr key={`${row.historyId}-${row.id}`}>
                              {/* 1. Time */}
                              <td>{formatDateTime(row.time)}</td>

                              {/* 2. Initiator */}
                              <td>
                                <div className="name-cell">
                                  {row.initiator?.brandName}
                                </div>
                              </td>

                              {/* 3. Listing */}
                              <td>
                                <div className="listing-cell">
                                  <img
                                    src={row.avatar}
                                    className="listing-img"
                                  />
                                  <div>
                                    <div className="stock-name" style={{ fontWeight: '400' }}>
                                      {row.stockName}
                                    </div>
                                    <div className="variant" style={{ fontSize: '10px' }}>{row.variant}</div>
                                    <div className="badges">
                                      <span className="badge badge-condition" style={{ padding: '2px 5px' }}>
                                        {row.condition}
                                      </span>
                                      <span className="badge badge-grading" style={{ padding: '2px 5px' }}>
                                        {row.grading}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              {/* 4. Base Price */}
                              <td>
                                <span className="price-before">
                                  {row.baseBefore}
                                </span>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="12"
                                  height="12"
                                  viewBox="0 0 12 12"
                                  fill="none"
                                  style={{
                                    marginTop: "-2px",
                                    marginLeft: "2px",
                                    marginRight: "2px",
                                  }}
                                >
                                  <path
                                    d="M2.5 6H9.5M9.5 6L6 2.5M9.5 6L6 9.5"
                                    stroke="#146C43"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                </svg>
                                <span className="price-after">
                                  {row.baseAfter}
                                </span>
                              </td>

                              {/* 5. Platforms */}
                              <td>
                                {row.platforms.map((p) => (
                                  <div key={p.name}>{p.name}</div>
                                ))}
                              </td>

                              {/* 6. ERP Price Changes */}
                              <td>
                                {row.platforms.map((p) => {
                                  const isSame = p.beforePrice === p.afterPrice;

                                  return (
                                    <div
                                      key={p.name}
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        marginBottom: "2px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {/* BEFORE PRICE */}
                                      <span
                                        style={{
                                          fontSize: "12px",
                                          color: "#146C43",
                                          fontWeight: 400,
                                        }}
                                      >
                                        {p.beforePrice ?? '-'}
                                      </span>

                                      {/* ARROW — only if changed */}
                                      {/* ( */}
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                      >
                                        <path
                                          d="M2.5 6H9.5M9.5 6L6 2.5M9.5 6L6 9.5"
                                          stroke="#146C43"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        />
                                      </svg>
                                      {/* ) */}

                                      {/* AFTER PRICE */}
                                      <span
                                        style={{
                                          padding: "2px 6px",
                                          borderRadius: "12px",
                                          fontSize: "12px",
                                          fontWeight: 600,
                                          color: "#146C43",
                                          border: "1px solid #146C43",
                                          background: "#146C4322",
                                          lineHeight: "16px",
                                        }}
                                      >
                                        {p.afterPrice}
                                      </span>
                                    </div>
                                  );
                                })}
                              </td>


                              {/* 7. Reason */}
                              <td
                                style={{
                                  maxWidth: "100px",   // adjust as needed
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                  whiteSpace: "pre-line"
                                }}
                              >
                                {row.reason || "NA"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {flattenedPriceHistory.length > 0 && (
                    <div className="pagination" style={{ marginTop: 16 }}>
                      <div className="pagination-info">
                        {`${(phCurrentPage - 1) * PH_PAGE_SIZE + 1} - ${Math.min(
                          phCurrentPage * PH_PAGE_SIZE,
                          flattenedPriceHistory.length,
                        )} of ${flattenedPriceHistory.length} items`}
                      </div>

                      <Pagination
                        currentPage={phCurrentPage}
                        totalPages={phTotalPages}
                        onPageChange={setPhCurrentPage}
                      />
                    </div>
                  )}

                  {/* Drawer — uses same class/structure as your existing drawer (not a centered modal) */}
                  {phDrawerOpen && phSelectedRow && (
                    <>
                      <div className="drawer-overlay" />
                      <div className="entity-drawer" ref={phDrawerRef}>
                        <div className="drawer-header">
                          <h3>Price History</h3>
                          <button
                            className="close-btn"
                            onClick={() => {
                              setPhDrawerOpen(false);
                              setPhSelectedRow(null);
                            }}
                          >
                            <X size={24} />
                          </button>
                        </div>

                        <div className="main-drawer-inner">
                          <div className="drawer-title d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                              <h2 className="pb-0">
                                Pricing Change ({phSelectedRow.changeCount})
                              </h2>
                            </div>
                          </div>

                          <div className="drawer-tabs">
                            <button
                              className={
                                phDrawerTab === "overview" ? "active" : ""
                              }
                              onClick={() => setPhDrawerTab("overview")}
                            >
                              Overview
                            </button>
                            <button
                              className={
                                phDrawerTab === "changes" ? "active" : ""
                              }
                              onClick={() => setPhDrawerTab("changes")}
                            >
                              Changes
                            </button>
                          </div>

                          <div className="drawer-content">
                            {/* Overview Tab: two fields from selected row + one fallback static only when reason not present */}
                            {phDrawerTab === "overview" && (
                              <div className="tab-panel entity-info-panel">
                                <div className="field-block">
                                  <div className="field-label">Time</div>
                                  <div className="field-value">
                                    {phSelectedRow.time}
                                  </div>
                                </div>

                                <div className="field-block">
                                  <div className="field-label">Initiator</div>
                                  <div className="field-value">
                                    {phSelectedRow.initiator?.brandName}
                                  </div>
                                </div>

                                <div className="field-block">
                                  <div className="field-label">Reason</div>
                                  <div className="field-value">
                                    {phSelectedRow.reason ??
                                      "Pricing Change (system)"}{" "}
                                    {/* fallback static only if reason missing */}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Changes Tab: table of changes (pulled from selectedRow.changes) */}
                            {phDrawerTab === "changes" && (
                              <div className="tab-panel pricing-panel">
                                <div
                                  className="entities-tab"
                                  style={{ padding: 0, paddingTop: "20px" }}
                                >
                                  <div className="table-wrapper">
                                    <table className="entities-table">
                                      <thead>
                                        <tr>
                                          <th>
                                            <span>
                                              Stock <ChevronsUpDown size={12} />
                                            </span>
                                          </th>
                                          <th>
                                            <span>
                                              Base Price{" "}
                                              <ChevronsUpDown size={12} />
                                            </span>
                                          </th>
                                          <th>
                                            <span>
                                              Platform Price{" "}
                                              <ChevronsUpDown size={12} />
                                            </span>
                                          </th>
                                          <th>
                                            <span>
                                              ERP Price{" "}
                                              <ChevronsUpDown size={12} />
                                            </span>
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {sampleRows.map((item, idx) => (
                                          <tr
                                            key={item.id}
                                            className={
                                              idx % 2 === 1 ? "striped" : ""
                                            }
                                          >
                                            {/* Stock */}
                                            <td>
                                              <div
                                                className="name-cell"
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 10,
                                                }}
                                              >
                                                <img
                                                  src={
                                                    item.avatar || safeNoImage
                                                  }
                                                  alt=""
                                                  style={{
                                                    width: 40,
                                                    height: 40,
                                                    marginRight: 8,
                                                  }}
                                                />
                                                <div>
                                                  <div>{item.name}</div>
                                                  <div className="sub-line mt-1">
                                                    {item.variant}
                                                  </div>
                                                  <div
                                                    style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 6,
                                                      marginTop: 6,
                                                    }}
                                                  >
                                                    <span
                                                      style={{
                                                        background: "#D1E7DD",
                                                        color: "#198754",
                                                        border:
                                                          "1px solid #198754",
                                                        padding: "2px 8px",
                                                        fontSize: 8,
                                                        lineHeight: "normal",
                                                        borderRadius: 12,
                                                        fontWeight: 600,
                                                      }}
                                                    >
                                                      {item.condition}
                                                    </span>
                                                    <span className="sub-line m-0">
                                                      {item.grading}
                                                    </span>
                                                  </div>
                                                </div>
                                              </div>
                                            </td>

                                            {/* Base Price */}
                                            <td>
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: "6px",
                                                }}
                                              >
                                                <span style={{ fontSize: 14 }}>
                                                  {item.baseCurrent}
                                                </span>
                                                <Arrow />
                                                <PricePill
                                                  value={item.baseTarget}
                                                />
                                              </div>
                                            </td>

                                            {/* Platform Price */}
                                            <td>
                                              <PlatformList
                                                platforms={item.platforms}
                                              />
                                            </td>

                                            {/* ERP Price */}
                                            <td>
                                              <ErpList erp={item.erp} />
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <style>
        {`
          .listing-cell {
  display: flex;
  gap: 10px;
}

.listing-img {
  width: 40px;
  height: 40px;

}

.stock-name {
  font-weight: 600;
}

.variant {
  font-size: 12px;
  color: #6c757d;
}

.badge-condition {
  margin-right: 6px;
  border: 1px solid #FFEB00;
  background: #FFEB00;
  color: #212529;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 8px;
  border-radius: 99px;
  padding: 2px 5px,
}

.badge-grading {
 margin-right: 6px;
  border: 1px solid #FFEB00;
  background: #FFEB00;
  color: #212529;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-weight: 600;
  font-size: 8px;
  border-radius: 99px;
  padding: 2px 5px,
}

.price-before {
   color: #146C43;
}

.price-after {
  color: var(--Utilised-success-2, #146C43);
text-align: center;

/* Button */
font-family: Montserrat;
font-size: 12px;
font-style: normal;
font-weight: 600;
line-height: normal;
padding: 2px 5px;
justify-content: center;
align-items: center;
gap: 10px;
border-radius: 99px;
border: 1px solid var(--Utilised-success-2, #146C43);
background: var(--Utilised-success-1, #D1E7DD);
}

.table-striped > tbody > tr:nth-of-type(odd) {
  --bs-table-accent-bg: transparent;
  background-color: #fff;
}

.table-striped > tbody > tr:nth-of-type(even) {
  --bs-table-accent-bg: rgba(68, 67, 67, 0.03);
  background-color: rgba(68, 67, 67, 0.03);
}

.entities-tab .table-wrapper{
    border-bottom:none;
}

        `}
      </style>
    </div>
  );
}